import React, { useEffect, useState, useRef } from 'react';
import { Button, Card, CardBody, Label, FormGroup, Input, Row, Col, Collapse } from 'reactstrap';
import moment from 'moment';
import ReactDatetime from 'react-datetime';
import { formatTimestamp } from '../../helpers/formatter';
import FedexRateOption from './FedexRateOption';
import { UpgradeHint } from './featureLock';
import fedex_logo from '../../assets/img/Fedex-logo.png';

const PriceChoices = ({
    showForm2,
    address,
    standardPrice,
    expeditedPrice,
    superExpeditedPrice,
    oneDayThreePrice,
    confidencePercent,
    totalDriverCount,
    nearbyDriverLocked = false,
    weatherCondition,
    setIsScheduled,
    setIsExpedited,
    setIsSuperExpedited,
    price,
    setPrice,
    priceChoice,
    setIsProceeded,
    errorMessageSubmit,
    scheduledTime,
    setScheduledTime,
    isScheduled,
    setScheduledDate,
    scheduledFor,
    errors,
    vehicleType,
    isProceeded,
    handleSubmit,
    standardPriceRangeLow,
    standardPriceRangeHigh,
    expeditedPriceRangeLow,
    expeditedPriceRangeHigh,
    superExpeditedPriceRangeLow,
    superExpeditedPriceRangeHigh,
    priceRangeLow,
    hasTqlRateValue,
    LTLPrice,
    LTLLeadTime,
    setIsLTL,
    isLTL,
    setVehicleType,
    recommendedVehicleType,
    fedexPricesOptions = [],
    setIsFedex,
    setFedexSelection,
    setIsShipTomorrow = () => {},
    isActiveEdit = false
}) => {
    const [showFedex, setShowFedex] = useState(false);
    let now = moment();
    let tomorrow = moment().add(1, 'day');
    let limit = moment().add(31, 'day');
    const expeditedAfterHoursMarkupRate = 0.25;
    const expeditedAfterHoursMarkupMultiplier = 1 + expeditedAfterHoursMarkupRate;
    const isWeekend = now.day() === 0 || now.day() === 6;
    const hasImmediateExpeditedSurcharge = isWeekend || now.hour() >= 18;
    const hasPostMidnightExpeditedSurcharge = now.hour() < 4;

    useEffect(() => {
        if (fedexPricesOptions && fedexPricesOptions.length > 0) {
            setShowFedex(true);
        } else {
            setShowFedex(false);
        }
    }, [fedexPricesOptions]);

    const handleScheduleTime = event => {
        setScheduledTime(event.target.value);
    };

    const applyAfterHoursMarkup = (amount, shouldApplyMarkup) => {
        if (!shouldApplyMarkup || typeof amount !== 'number' || Number.isNaN(amount)) {
            return amount;
        }

        return Number((amount * expeditedAfterHoursMarkupMultiplier).toFixed(2));
    };

    const getScheduledWindowMoment = timeValue => {
        const baseMoment = scheduledFor ? moment(scheduledFor) : moment(tomorrow);
        return baseMoment.clone().hour(timeValue).minute(0).second(0).millisecond(0);
    };

    const shouldApplyScheduledExpeditedSurcharge = (timeValue, expedite, superExpedite) => {
        if (!expedite || superExpedite) {
            return false;
        }

        const scheduledMoment = getScheduledWindowMoment(timeValue);
        return scheduledMoment.day() === 0 || scheduledMoment.day() === 6 || scheduledMoment.hour() >= 18;
    };

    const matchesScheduledTime = timeValue => String(scheduledTime) === String(timeValue);
    const hasSameNumericValue = (left, right) => Number(left) === Number(right);

    const getImmediateExpeditedMarkupMultiplier = () => {
        let multiplier = 1;

        if (hasImmediateExpeditedSurcharge) {
            multiplier += expeditedAfterHoursMarkupRate;
        }

        if (hasPostMidnightExpeditedSurcharge) {
            multiplier += expeditedAfterHoursMarkupRate;
        }

        return multiplier;
    };

    const applyImmediateExpeditedMarkup = amount => {
        if (typeof amount !== 'number' || Number.isNaN(amount)) {
            return amount;
        }

        return Number((amount * getImmediateExpeditedMarkupMultiplier()).toFixed(2));
    };

    const immediateExpeditedPrice = applyImmediateExpeditedMarkup(expeditedPrice);
    const immediateExpeditedPriceRangeLow = applyImmediateExpeditedMarkup(expeditedPriceRangeLow);
    const immediateExpeditedPriceRangeHigh = applyImmediateExpeditedMarkup(expeditedPriceRangeHigh);
    const immediateExpeditedSurchargeLabel = hasPostMidnightExpeditedSurcharge
        ? 'Includes after-hours/weekend surcharge and post-midnight surcharge'
        : hasImmediateExpeditedSurcharge
          ? 'Includes after-hours/weekend surcharge'
          : null;

    const verifyDate = (current, selected) => {
        // return current.day() !== 0 && current.isAfter(now) && limit.isAfter(current);
        return current.isAfter(now) && limit.isAfter(current); //! remove sunday
    };

    const usePrevious = value => {
        const ref = useRef();
        useEffect(() => {
            ref.current = value;
        });
        return ref.current;
    };
    const previousStandardPrice = usePrevious(standardPrice);
    const previousVehicleType = usePrevious(vehicleType);
    const previousIsLTL = usePrevious(isLTL);
    const previousStandardPriceRangeLow = usePrevious(standardPriceRangeLow);
    const hasNumericValue = value => value !== '' && value !== null && value !== undefined && !Number.isNaN(parseFloat(value));
    const hasRangeQuote = (rangeLow, rangeHigh) => hasNumericValue(rangeLow) && hasNumericValue(rangeHigh);
    const hasDirectQuote = value => hasNumericValue(value);
    const isPlaceholderRangeQuote = (rangeLow, rangeHigh) => Number(rangeLow) === 0 && Number(rangeHigh) === 0;
    const requiresRangeQuote = !isActiveEdit &&
        (((vehicleType === '18_wheeler_dry_van' || vehicleType === '18_wheeler_flat_bed' || vehicleType === '18_wheeler_reefer') && !hasTqlRateValue) ||
            vehicleType === 'car_hauler');
    const hasVisibleRangeQuote = (rangeLow, rangeHigh) => hasRangeQuote(rangeLow, rangeHigh) && !isPlaceholderRangeQuote(rangeLow, rangeHigh);
    const hasStandardQuote = requiresRangeQuote ? hasVisibleRangeQuote(standardPriceRangeLow, standardPriceRangeHigh) : hasDirectQuote(standardPrice);
    const hasExpeditedQuote = requiresRangeQuote ? hasVisibleRangeQuote(expeditedPriceRangeLow, expeditedPriceRangeHigh) : hasDirectQuote(expeditedPrice);
    const formatDirectQuote = value => (hasDirectQuote(value) ? `$${parseFloat(value).toFixed(2)}` : 'Calculating quote...');
    const formatRangeQuote = (rangeLow, rangeHigh) =>
        hasVisibleRangeQuote(rangeLow, rangeHigh)
            ? `Estimate $${parseFloat(rangeLow).toFixed(2)} -- ${parseFloat(rangeHigh).toFixed(2)}`
            : 'Calculating estimate...';

    useEffect(() => {
        if (isLTL) {
            if (vehicleType !== '18_wheeler_dry_van') {
                setVehicleType('18_wheeler_dry_van');
            }
            return;
        }

        if (previousIsLTL) {
            if (previousVehicleType && previousVehicleType !== '18_wheeler_dry_van') {
                setVehicleType(previousVehicleType);
            } else if (recommendedVehicleType && recommendedVehicleType !== vehicleType) {
                setVehicleType(recommendedVehicleType);
            }
        }
    }, [isLTL, previousIsLTL, previousVehicleType, recommendedVehicleType, setVehicleType, vehicleType]);

    useEffect(() => {
        // Scheduled orders manage isProceeded in the parent via the selected date+time, so don't
        // let a quote re-fetch or AI vehicle re-selection here flip Proceed back to grey.
        if (!isLTL && !isScheduled) {
            if (previousStandardPrice !== standardPrice || previousStandardPriceRangeLow !== standardPriceRangeLow || previousVehicleType !== vehicleType) {
                setIsProceeded(false);
            } else {
                setIsProceeded(true);
            }
        }
    }, [standardPrice, standardPriceRangeLow, isLTL, isScheduled]);

    const ScheduleTimeOptions = ({ timeValue, price, expedite, superExpedite, timeLabel, priceRangeLow, priceRangeHigh }) => {
        const hasQuote = requiresRangeQuote ? hasVisibleRangeQuote(priceRangeLow, priceRangeHigh) : hasDirectQuote(price);

        if (!hasQuote) {
            return null;
        }

        const shouldApplyMarkup = shouldApplyScheduledExpeditedSurcharge(timeValue, expedite, superExpedite);
        const displayedPrice = applyAfterHoursMarkup(price, shouldApplyMarkup);
        const displayedPriceRangeLow = applyAfterHoursMarkup(priceRangeLow, shouldApplyMarkup);
        const displayedPriceRangeHigh = applyAfterHoursMarkup(priceRangeHigh, shouldApplyMarkup);

        return (
            <FormGroup check className="form-check-radio" id="schedule-time">
                <Label check>
                    <Input
                        required
                        type="radio"
                        name="exampleRadio4"
                        id="exampleRadios4"
                        value={timeValue}
                        onChange={e => {
                            handleScheduleTime(e);
                            setPrice(displayedPrice);
                            setIsExpedited(expedite);
                            setIsSuperExpedited(superExpedite);
                            setIsShipTomorrow(false);
                            setIsLTL(false);
                            setIsFedex(false);
                        }}
                        checked={matchesScheduledTime(timeValue)}
                    />
                    <p id="radio" className="form-check-sign">
                        {timeLabel}
                        {requiresRangeQuote ? (
                            <span style={{ color: '#f7941d' }}>
                                Estimate ${parseFloat(displayedPriceRangeLow).toFixed(2)} -- {parseFloat(displayedPriceRangeHigh).toFixed(2)}
                            </span>
                        ) : (
                            <span style={{ color: '#f7941d' }}>${parseFloat(displayedPrice).toFixed(2)}</span>
                        )}
                        {shouldApplyMarkup ? <span style={{ color: '#f7941d', marginLeft: '6px' }}>(includes after-hours/weekend surcharge)</span> : null}
                    </p>
                </Label>
            </FormGroup>
        );
    };

    const LTLOption = () => {
        if (!LTLPrice) {
            return null;
        }
        let pickTime = now.add(LTLLeadTime, 'hour');
        return (
            <FormGroup check className="form-check-radio" id="shipment-price">
                <Label check>
                    <Input
                        required
                        id="ltl-price"
                        type="radio"
                        name="exampleRadio3"
                        value={LTLPrice}
                        onChange={() => {
                            setIsScheduled(false);
                            setIsExpedited(false);
                            setIsSuperExpedited(false);
                            setIsShipTomorrow(false);
                            setPrice(LTLPrice);
                            setIsLTL(true);
                            setIsFedex(false);
                            priceChoice(standardPrice, expeditedPrice, superExpeditedPrice);
                            setIsProceeded(true);
                        }}
                        checked={isLTL}
                    />
                    <span className="form-check-sign" /> LTL Price :<div style={{ color: '#f7941d' }}>${parseFloat(LTLPrice).toFixed(2)}</div>
                    (Orders will be picked up <span style={{ color: '#f7941d' }}>after {formatTimestamp(pickTime)}</span> )
                </Label>
            </FormGroup>
        );
    };

    const StandardOn = () => {
        return (
            <FormGroup check className="form-check-radio" id="shipment-price">
                <Label check>
                    <Input
                        required
                        id="standard-price"
                        type="radio"
                        name="exampleRadio3"
                        value={standardPrice}
                        onChange={() => {
                            setIsScheduled(false);
                            setIsExpedited(false);
                            setIsSuperExpedited(false);
                            setIsShipTomorrow(false);
                            setPrice(standardPrice);
                            setIsLTL(false);
                            setIsFedex(false);
                            priceChoice(standardPrice, expeditedPrice, superExpeditedPrice);
                            setIsProceeded(true);
                        }}
                        disabled={!hasStandardQuote}
                        checked={
                            requiresRangeQuote
                                ? hasSameNumericValue(priceRangeLow, standardPriceRangeLow) && isScheduled === false && isProceeded === true
                                : hasSameNumericValue(price, standardPrice) && isScheduled === false && isProceeded === true
                        }
                    />
                    <span className="form-check-sign" /> Standard Price Today:{' '}
                    {requiresRangeQuote ? (
                        <div style={{ color: '#f7941d' }}>
                            {formatRangeQuote(standardPriceRangeLow, standardPriceRangeHigh)}
                            {hasStandardQuote ? ', we will get you accurate quote back asap' : ''}
                        </div>
                    ) : (
                        <div style={{ color: '#f7941d' }}>{formatDirectQuote(standardPrice)}</div>
                    )}
                    (Orders placed <span style={{ color: '#f7941d' }}>before 5 PM</span> will be delivered the <span style={{ color: '#f7941d' }}>same day</span>)
                </Label>
            </FormGroup>
        );
    };

    const StandardOff = ({ limitVehicle = false }) => {
        return (
            <FormGroup check className="form-check-radio" id="shipment-price">
                <Label check>
                    <Input
                        required
                        id="standard-price"
                        type="radio"
                        name="exampleRadio3"
                        value={standardPrice}
                        onClick={() => {
                            setIsExpedited(false);
                            setIsScheduled(false);
                            setIsSuperExpedited(false);
                            setIsLTL(false);
                            setIsFedex(false);
                            setPrice(standardPrice);
                            priceChoice(standardPrice, expeditedPrice, superExpeditedPrice);
                        }}
                        disabled
                    />

                    <div style={{ color: 'gray' }}>
                        <span className="form-check-sign" /> Standard Price Today:{' '}
                        {requiresRangeQuote ? (
                            <div style={{ color: '#gray' }}>
                                {formatRangeQuote(standardPriceRangeLow, standardPriceRangeHigh)}
                            </div>
                        ) : (
                            <div style={{ color: 'gray' }}>{formatDirectQuote(standardPrice)}</div>
                        )}{' '}
                        (<span style={{ color: 'gray' }}>If you would like the same day delivery for order placed after {limitVehicle ? 1 : 5} PM, </span>{' '}
                        <span style={{ color: '#f7941d' }}>please choose expedited</span>)
                    </div>
                </Label>
            </FormGroup>
        );
    };

    const OneDayThreeOption = () => {
        if (!oneDayThreePrice || !hasNumericValue(oneDayThreePrice)) return null;
        return (
            <FormGroup check className="form-check-radio" id="shipment-price">
                <Label check>
                    <Input
                        required
                        id="one-day-three-price"
                        type="radio"
                        name="exampleRadio3"
                        value={oneDayThreePrice}
                        onChange={() => {
                            setIsScheduled(false);
                            setIsExpedited(false);
                            setIsSuperExpedited(false);
                            setIsShipTomorrow(true);
                            setPrice(oneDayThreePrice);
                            setIsLTL(false);
                            setIsFedex && setIsFedex(false);
                            priceChoice(standardPrice, expeditedPrice, superExpeditedPrice);
                            setIsProceeded(true);
                        }}
                        checked={hasSameNumericValue(price, oneDayThreePrice) && isScheduled === false && isProceeded === true}
                    />
                    <span className="form-check-sign" /> 1–3 Day Delivery:{' '}
                    <div style={{ color: '#f7941d' }}>
                        {requiresRangeQuote
                            ? `Starting at $${parseFloat(oneDayThreePrice).toFixed(2)}`
                            : `$${parseFloat(oneDayThreePrice).toFixed(2)}`}
                    </div>
                    (Pickup next business day — <span style={{ color: '#f7941d' }}>flexible scheduling</span>)
                </Label>
            </FormGroup>
        );
    };

    // TQL-backed vehicle types have national coverage — always allow expedited
    const tqlVehicleTypes = new Set(['sprinter_van', 'box_truck', 'gooseneck_trailer']);
    const expeditedAllowed = tqlVehicleTypes.has(vehicleType) || confidencePercent == null || confidencePercent >= 80;

    const ExpediteOn = () => {
        if (!expeditedAllowed) {
            return (
                <FormGroup check className="form-check-radio" id="shipment-price">
                    <Label check>
                        <Input required id="expedited-price" type="radio" name="exampleRadio3" disabled />
                        <div style={{ color: 'rgba(255,255,255,0.35)' }}>
                            <span className="form-check-sign" /> Expedited Price Today:{' '}
                            <div style={{ color: 'rgba(255,255,255,0.35)' }}>{formatDirectQuote(expeditedPrice)}</div>
                            <div style={{ fontSize: 11, fontStyle: 'italic', color: '#f44336' }}>
                                Not available — limited drivers in area
                            </div>
                        </div>
                    </Label>
                </FormGroup>
            );
        }
        return (
            <FormGroup check className="form-check-radio" id="shipment-price">                <Label check>
                    <Input
                        required
                        id="expedited-price"
                        type="radio"
                        name="exampleRadio3"
                        value={expeditedPrice}
                        onChange={() => {
                            setIsScheduled(false);
                            setIsExpedited(true);
                            setIsSuperExpedited(false);
                            setIsShipTomorrow(false);
                            setIsLTL(false);
                            setIsFedex(false);
                            setPrice(immediateExpeditedPrice);
                            priceChoice(standardPrice, expeditedPrice, superExpeditedPrice);
                            setIsProceeded(true);
                        }}
                        disabled={!hasExpeditedQuote}
                        checked={
                            requiresRangeQuote
                                ? hasSameNumericValue(priceRangeLow, immediateExpeditedPriceRangeLow) && isScheduled === false && isProceeded === true
                                : hasSameNumericValue(price, immediateExpeditedPrice) && isScheduled === false && isProceeded === true
                        }
                    />
                    <span className="form-check-sign" /> Expedited Price Today:{' '}
                    {requiresRangeQuote ? (
                        <>
                            <div style={{ color: '#f7941d' }}>
                                {formatRangeQuote(immediateExpeditedPriceRangeLow, immediateExpeditedPriceRangeHigh)}
                                {hasExpeditedQuote ? ', we will get you accurate quote back asap' : ''}
                            </div>
                            (<span style={{ color: '#f7941d' }}>Driver will be </span> dispatched <span style={{ color: '#f7941d' }}>upon approval</span>)
                        </>
                    ) : (
                        <>
                            <div style={{ color: '#f7941d' }}>{formatDirectQuote(immediateExpeditedPrice)}</div>(<span style={{ color: '#f7941d' }}>Driver will be </span> dispatched{' '}
                            <span style={{ color: '#f7941d' }}>immediately</span>)
                        </>
                    )}
                    {immediateExpeditedSurchargeLabel ? <div style={{ color: '#f7941d' }}>{immediateExpeditedSurchargeLabel}</div> : null}
                </Label>
            </FormGroup>
        );
    };

    return (
        <>
            <Collapse id="collapse2" isOpen={showForm2} style={{ marginTop: '10px' }}>
                <Card style={{ backgroundColor: '#344674', color: '#fff' }} className="card-form">
                    <CardBody>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                            <h4 style={{ fontSize: '20px', margin: 0 }}>Shipment Quote</h4>
                            {confidencePercent != null && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                                        <span style={{
                                            fontWeight: 700,
                                            color: confidencePercent >= 90 ? '#4caf50'
                                                : confidencePercent >= 85 ? '#ff9800'
                                                : '#f44336',
                                        }}>
                                            {confidencePercent}% Confidence
                                        </span>
                                        {nearbyDriverLocked ? (
                                            <UpgradeHint label="Upgrade to Pro to see drivers nearby" style={{ marginTop: 0, display: 'inline-block', fontSize: 12 }} />
                                        ) : (totalDriverCount != null && (
                                            <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>
                                                {totalDriverCount < 5 ? 'Limited drivers in area' : `${totalDriverCount} drivers in area`}
                                            </span>
                                        ))}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>
                                        Likelihood of driver match within 30 min
                                        {weatherCondition && weatherCondition !== 'Clear' && weatherCondition !== 'Cloudy' && weatherCondition !== 'Unavailable' && weatherCondition !== 'Unknown' && (
                                            <span style={{ marginLeft: 6, color: '#ff9800' }}>· {weatherCondition}</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* <p>
              Deliver To <p style={{ color: '#f7941d' }}>{address}</p>
            </p> */}
                        <Row style={{ marginTop: '15px' }}>
                            <Col>
                                {now.hour() < 13 ? (
                                    <>
                                        <OneDayThreeOption />
                                        <StandardOn />
                                        <ExpediteOn />
                                    </>
                                ) : now.hour() < 17 ? (
                                    <>
                                        <OneDayThreeOption />
                                        {vehicleType === 'sprinter_van' ||
                                        vehicleType === 'box_truck' ||
                                        vehicleType === 'gooseneck_trailer' ||
                                        vehicleType === 'car_hauler' ||
                                        vehicleType === '18_wheeler_dry_van' ||
                                        vehicleType === '18_wheeler_flat_bed' ||
                                        vehicleType === '18_wheeler_reefer' ? (
                                            <StandardOff limitVehicle={true} />
                                        ) : (
                                            <StandardOn />
                                        )}
                                        <ExpediteOn />
                                    </>
                                ) : now.hour() >= 17 ? (
                                    <>
                                        <OneDayThreeOption />
                                        <StandardOff />
                                        <ExpediteOn />
                                    </>
                                ) : null}
                                <FormGroup check className="form-check-radio" id="shipment-price">
                                    <Label check>
                                        <Input
                                            required
                                            id="schedule-price"
                                            type="radio"
                                            name="exampleRadio3"
                                            // value={standardPrice}
                                            onChange={() => {
                                                setIsScheduled(true);
                                                setIsExpedited(false);
                                                setIsSuperExpedited(false);
                                                setIsShipTomorrow(false);
                                                setIsLTL(false);
                                                // priceChoice(standardPrice, expeditedPrice, superExpeditedPrice);
                                            }}
                                            checked={isScheduled}
                                        />
                                        <span className="form-check-sign"> Schedule For Different Date</span>
                                        <Collapse isOpen={true} hidden={!isScheduled} style={{ marginTop: '6px' }}>
                                            <Label className="form-check-label"></Label>
                                            <Row className="justify-content-md-center">
                                                <Col md="auto">
                                                    <FormGroup>
                                                        <ReactDatetime
                                                            input={false}
                                                            inputProps={{
                                                                className: 'form-control',
                                                                placeholder: formatTimestamp(tomorrow),
                                                                readOnly: true,
                                                                cursor: 'text'
                                                            }}
                                                            onChange={e => (e.toISOString ? setScheduledDate(e) : null)}
                                                            dateFormat="dddd, MMMM D, YYYY"
                                                            timeFormat={false}
                                                            isValidDate={(currentDate, selectedDate) => verifyDate(currentDate, selectedDate)}
                                                            // timeConstraints={scheduledFor ? (scheduledFor.day() === 6 ? saturdayTimeConstraint : weekdayTimeConstraint) : weekdayTimeConstraint}
                                                            defaultValue={tomorrow}
                                                            // closeOnSelect={true}
                                                            closeOnTab={true}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col style={{ marginTop: 35 }}>
                                                    <Row style={{ textAlign: 'center' }}>
                                                        <Col>
                                                            <p style={{ color: '#f7941d', fontSize: 12, fontWeight: 'bold' }}>Pick up Time:</p>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col md="auto" style={{ marginTop: 20 }}>
                                                            <ScheduleTimeOptions
                                                                timeValue={0}
                                                                price={superExpeditedPrice}
                                                                expedite={false}
                                                                superExpedite={true}
                                                                timeLabel="12 AM - 2 AM :"
                                                                priceRangeLow={superExpeditedPriceRangeLow}
                                                                priceRangeHigh={superExpeditedPriceRangeHigh}
                                                            />
                                                            <ScheduleTimeOptions
                                                                timeValue={2}
                                                                price={superExpeditedPrice}
                                                                expedite={false}
                                                                superExpedite={true}
                                                                timeLabel="2 AM - 4 AM :"
                                                                priceRangeLow={superExpeditedPriceRangeLow}
                                                                priceRangeHigh={superExpeditedPriceRangeHigh}
                                                            />
                                                            <ScheduleTimeOptions
                                                                timeValue={4}
                                                                price={expeditedPrice}
                                                                expedite={true}
                                                                superExpedite={false}
                                                                timeLabel="4 AM - 6 AM :"
                                                                priceRangeLow={expeditedPriceRangeLow}
                                                                priceRangeHigh={expeditedPriceRangeHigh}
                                                            />
                                                            <ScheduleTimeOptions
                                                                timeValue={6}
                                                                price={expeditedPrice}
                                                                expedite={true}
                                                                superExpedite={false}
                                                                timeLabel="6 AM - 8 AM :"
                                                                priceRangeLow={expeditedPriceRangeLow}
                                                                priceRangeHigh={expeditedPriceRangeHigh}
                                                            />
                                                            <ScheduleTimeOptions
                                                                timeValue={8}
                                                                price={standardPrice}
                                                                expedite={false}
                                                                superExpedite={false}
                                                                timeLabel="8 AM - 10 AM :"
                                                                priceRangeLow={standardPriceRangeLow}
                                                                priceRangeHigh={standardPriceRangeHigh}
                                                            />
                                                            <ScheduleTimeOptions
                                                                timeValue={10}
                                                                price={standardPrice}
                                                                expedite={false}
                                                                superExpedite={false}
                                                                timeLabel="10 AM - 12 PM :"
                                                                priceRangeLow={standardPriceRangeLow}
                                                                priceRangeHigh={standardPriceRangeHigh}
                                                            />
                                                        </Col>
                                                        <Col md="auto" style={{ marginTop: 20 }}>
                                                            <ScheduleTimeOptions
                                                                timeValue={12}
                                                                price={standardPrice}
                                                                expedite={false}
                                                                superExpedite={false}
                                                                timeLabel="12 PM - 2 PM :"
                                                                priceRangeLow={standardPriceRangeLow}
                                                                priceRangeHigh={standardPriceRangeHigh}
                                                            />
                                                            <ScheduleTimeOptions
                                                                timeValue={14}
                                                                price={standardPrice}
                                                                expedite={false}
                                                                superExpedite={false}
                                                                timeLabel="2 PM - 4 PM :"
                                                                priceRangeLow={standardPriceRangeLow}
                                                                priceRangeHigh={standardPriceRangeHigh}
                                                            />
                                                            <ScheduleTimeOptions
                                                                timeValue={16}
                                                                price={expeditedPrice}
                                                                expedite={true}
                                                                superExpedite={false}
                                                                timeLabel="4 PM - 6 PM :"
                                                                priceRangeLow={expeditedPriceRangeLow}
                                                                priceRangeHigh={expeditedPriceRangeHigh}
                                                            />
                                                            <ScheduleTimeOptions
                                                                timeValue={18}
                                                                price={expeditedPrice}
                                                                expedite={true}
                                                                superExpedite={false}
                                                                timeLabel="6 PM - 8 PM :"
                                                                priceRangeLow={expeditedPriceRangeLow}
                                                                priceRangeHigh={expeditedPriceRangeHigh}
                                                            />
                                                            <ScheduleTimeOptions
                                                                timeValue={20}
                                                                price={superExpeditedPrice}
                                                                expedite={false}
                                                                superExpedite={true}
                                                                timeLabel="8 PM - 10 PM :"
                                                                priceRangeLow={superExpeditedPriceRangeLow}
                                                                priceRangeHigh={superExpeditedPriceRangeHigh}
                                                            />
                                                            <ScheduleTimeOptions
                                                                timeValue={22}
                                                                price={superExpeditedPrice}
                                                                expedite={false}
                                                                superExpedite={true}
                                                                timeLabel="10 PM - 12 PM :"
                                                                priceRangeLow={superExpeditedPriceRangeLow}
                                                                priceRangeHigh={superExpeditedPriceRangeHigh}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                            <div hidden={errorMessageSubmit.errorCode !== 12}>
                                                <p style={{ color: 'red', marginTop: '4px' }}>{errorMessageSubmit.errorMessage}</p>
                                            </div>{' '}
                                            <div>
                                                {JSON.stringify(scheduledFor) && scheduledFor != null ? (
                                                    <div>
                                                        {' '}
                                                        <p style={{ marginBottom: 10 }}>
                                                            You select the scheduled <span style={{ color: '#f7941d' }}>pickup</span> time window:{' '}
                                                            <span style={{ color: '#f7941d' }}>
                                                                {scheduledFor
                                                                    ? JSON.stringify(
                                                                          moment(scheduledFor).format('dddd, MMMM-DD-YYYY, hh:mm A') +
                                                                              '--' +
                                                                              moment(scheduledFor)
                                                                                  .add(2, 'hours')
                                                                                  .format('hh:mm A')
                                                                      )
                                                                    : ''}
                                                            </span>
                                                        </p>
                                                        <p>
                                                            If you need <span style={{ color: '#f7941d' }}>pickup</span> at a specific time, please add it to{' '}
                                                            <span style={{ color: '#f7941d' }}>Pickup Instructions</span>
                                                        </p>
                                                        <p>
                                                            If you need <span style={{ color: 'green' }}>dropoff</span> at a specific time, please add it to{' '}
                                                            <span style={{ color: 'green' }}>Dropoff Instructions</span>
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        {/* <p>
                            Please select date and time window for delivery <span style={{ color: '#f7941d', fontWeight: 'bold' }}>to be picked up</span>
                          </p> */}
                                                        <p>
                                                            If you need <span style={{ color: '#f7941d' }}>pickup</span> at a specific time, please add it to{' '}
                                                            <span style={{ color: '#f7941d' }}>Pickup Instructions</span>
                                                        </p>
                                                        <p>
                                                            If you need <span style={{ color: 'green' }}>dropoff</span> at a specific time, please add it to{' '}
                                                            <span style={{ color: 'green' }}>Dropoff Instructions</span>
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </Collapse>
                                    </Label>
                                </FormGroup>
                                <LTLOption />
                            </Col>
                            <Col style={{ marginTop: '-40px' }}>
                                {showFedex && (
                                    <div style={{ marginTop: '15px', color: 'white' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <img src={fedex_logo} alt="FedEx Logo" style={{ width: '50px', height: '30px', marginLeft: '10px', objectFit: 'contain' }} />
                                            <h5 style={{ margin: 0 }}>Options</h5>
                                        </div>
                                        {fedexPricesOptions.map((rate, index) => (
                                            <FedexRateOption
                                                key={rate.service_type || rate.service_name || rate.id || `fedex-rate-${index}`}
                                                rate={rate}
                                                price={price}
                                                setPrice={setPrice}
                                                setIsScheduled={setIsScheduled}
                                                setIsExpedited={setIsExpedited}
                                                setIsSuperExpedited={setIsSuperExpedited}
                                                setIsLTL={setIsLTL}
                                                setIsProceeded={setIsProceeded}
                                                setFedexSelection={setFedexSelection}
                                                setIsFedex={setIsFedex}
                                            />
                                        ))}
                                    </div>
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <div hidden={!errors.price}>
                                    <p style={{ color: 'red', marginTop: '4px' }}>Please select a shipping preference</p>
                                </div>
                                {vehicleType ? null : <p style={{ color: 'red', marginTop: '4px' }}>Please select a vehicle for delivery</p>}
                                {isProceeded ? (
                                    <Button
                                        className="btn submit-btn"
                                        color="danger"
                                        onClick={() => {
                                            handleSubmit();
                                            window.scrollTo(0, 0);
                                            // console.log('click handle submit');
                                            // history.push('/admin/delivery_creation/#form-top');
                                        }}
                                        style={{ marginTop: '10px' }}
                                    >
                                        Proceed
                                    </Button>
                                ) : (
                                    <Button className="btn submit-btn" style={{ color: 'gray', marginTop: '10px' }} onClick={() => {}}>
                                        Proceed
                                    </Button>
                                )}

                                <div hidden={errorMessageSubmit.errorCode !== 10}>
                                    <p style={{ color: 'red', marginTop: '4px' }}>{errorMessageSubmit.errorMessage}</p>
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Collapse>
        </>
    );
};

export default PriceChoices;
